import React, { FC } from 'react';
import { Segment } from '@tg/core/components';
import classNames from 'classnames';
import { Link, LinkProps } from 'react-router-dom';

interface ListHeading {
  id?: string | number;
  text?: string;
  align?: 'left' | 'right';
  width?: string;
}
interface ListItem {
  id: string;
  columns: (string | JSX.Element)[];
  to?: LinkProps['to'];
}

interface Props {
  headings?: ListHeading[];
  items: ListItem[];
  noBorder?: boolean;
  noShadow?: boolean;
  type?: string;
}

/**
 * A list laid out like a table, with headings
 */
const List: FC<Props> = ({ items, headings, noShadow, noBorder, type }) => {
  /**
   * Share the grid column widths between items and headers
   */
  const colWidths = headings
    ? headings.map(({ width }) => width)
    : items[0].columns.map(() => null);
  const gridColumns: string[] = colWidths.map((i, index) => {
    let value = 'minmax(0, 1fr)';
    if (type === 'popup' && index !== 0) {
      value = 'minmax(0, 2fr)';
    }
    return i || value;
  });

  const gridClasses = 'flex grid content-between gap-5';

  // We can't use `text-${heading.align}` as purgeCSS wouldn't recognise it
  const alignCss = {
    left: 'text-left',
    right: 'text-right',
  };

  const getCellClassNames = (index: number) => {
    let whiteSpaceClass;
    if (type === 'payroll') {
      whiteSpaceClass = '';
    } else if (type !== 'popup' && type !== 'ofcPopup' && type !== 'salary') {
      whiteSpaceClass = 'whitespace-nowrap';
    }
    return classNames(whiteSpaceClass, {
      'text-right': headings && headings[index].align === 'right',
    });
  };

  const Wrapper = ({ to, children, ...rest }) => {
    if (to) {
      return (
        <Link to={to} {...rest}>
          {children}
        </Link>
      );
    }
    return <div {...rest}>{children}</div>;
  };

  const list = (
    <ul className='divide-y divide-gray-200'>
      {items.map(({ id, columns, to, faded }) => (
        <li key={id}>
          <Wrapper
            to={to}
            className={classNames(
              'py-3 items-center text-current',
              gridClasses,
              {
                'px-3': !noBorder,
              },
            )}
            style={{ gridTemplateColumns: gridColumns.join(' ') }}
          >
            {columns.map((column, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${id}_${index}`}
                className={getCellClassNames(index)}
                aria-describedby={`list_heading_${index}`}
                style={{ opacity: faded ? '0.5' : '' }}
              >
                {column}
              </div>
            ))}
          </Wrapper>
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      {headings && (
        <div
          style={{ gridTemplateColumns: gridColumns.join(' ') }}
          className={classNames('font-semibold text-sm mb-1 mx-3', gridClasses)}
        >
          {headings.map((heading, index) => (
            <div
              className={`${
                alignCss[heading.align || 'left']
              } whitespace-nowrap`}
              id={`list_heading_${index}`}
              // eslint-disable-next-line react/no-array-index-key
              key={heading.id || index}
            >
              {heading.text}
            </div>
          ))}
        </div>
      )}

      {noBorder ? (
        <div>{list}</div>
      ) : (
        <Segment flush shadowLevel={noShadow ? 'none' : undefined}>
          {list}
        </Segment>
      )}
    </div>
  );
};

export default List;
